import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';

// Async thunks
export const getUserApplications = createAsyncThunk(
    'taskApplications/getUserApplications',
    async (params: { status?: string; page?: number; limit?: number } = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append('status', params.status);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit) queryParams.append('limit', params.limit.toString());

        const response = await api.get(`/task-applications/my-applications?${queryParams}`);
        return response.data;
    }
);

export const applyToTask = createAsyncThunk(
    'taskApplications/applyToTask',
    async ({ taskId, applicationMessage }: { taskId: string; applicationMessage?: string }) => {
        const response = await api.post(`/task-applications/apply/${taskId}`, {
            applicationMessage
        });
        return response.data;
    }
);

export const getTaskApplications = createAsyncThunk(
    'taskApplications/getTaskApplications',
    async ({ taskId, status, page, limit }: { 
        taskId: string; 
        status?: string; 
        page?: number; 
        limit?: number; 
    }) => {
        const queryParams = new URLSearchParams();
        if (status) queryParams.append('status', status);
        if (page) queryParams.append('page', page.toString());
        if (limit) queryParams.append('limit', limit.toString());

        const response = await api.get(`/task-applications/task/${taskId}?${queryParams}`);
        return response.data;
    }
);

export const reviewApplication = createAsyncThunk(
    'taskApplications/reviewApplication',
    async ({ 
        applicationId, 
        action, 
        designation, 
        rejectionReason 
    }: { 
        applicationId: string; 
        action: 'approve' | 'reject'; 
        designation?: string; 
        rejectionReason?: string; 
    }) => {
        const response = await api.put(`/task-applications/review/${applicationId}`, {
            action,
            designation,
            rejectionReason
        });
        return response.data;
    }
);

export const inviteUsersToTask = createAsyncThunk(
    'taskApplications/inviteUsersToTask',
    async ({ taskId, userIds }: { taskId: string; userIds?: string[] }) => {
        const response = await api.post(`/task-applications/invite/${taskId}`, {
            userIds
        });
        return response.data;
    }
);

interface TaskApplication {
    _id: string;
    task: {
        _id: string;
        title: string;
        description: string;
        taskUrl: string;
        totalBudget: number;
        status: string;
        company: {
            _id: string;
            companyName: string;
            logo?: string;
        };
    };
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        skill: {
            _id: string;
            name: string;
        };
        region: string;
        personalDetails: {
            address: {
                state?: string;
                district?: string;
            };
        };
    };
    status: 'invited' | 'applied' | 'approved' | 'rejected';
    appliedAt?: string;
    reviewedAt?: string;
    reviewedBy?: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    assignedDesignation?: string;
    applicationMessage?: string;
    rejectionReason?: string;
    createdAt: string;
    updatedAt: string;
}

interface TaskApplicationState {
    userApplications: TaskApplication[];
    taskApplications: TaskApplication[];
    loading: boolean;
    error: string | null;
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    };
}

const initialState: TaskApplicationState = {
    userApplications: [],
    taskApplications: [],
    loading: false,
    error: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
    }
};

const taskApplicationSlice = createSlice({
    name: 'taskApplications',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearUserApplications: (state) => {
            state.userApplications = [];
        },
        clearTaskApplications: (state) => {
            state.taskApplications = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // Get user applications
            .addCase(getUserApplications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUserApplications.fulfilled, (state, action) => {
                state.loading = false;
                state.userApplications = action.payload.data.applications;
                state.pagination = action.payload.data.pagination;
            })
            .addCase(getUserApplications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch applications';
            })
            
            // Apply to task
            .addCase(applyToTask.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(applyToTask.fulfilled, (state, action) => {
                state.loading = false;
                // Add new application to the list
                state.userApplications.unshift(action.payload.data);
            })
            .addCase(applyToTask.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to apply to task';
            })
            
            // Get task applications
            .addCase(getTaskApplications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTaskApplications.fulfilled, (state, action) => {
                state.loading = false;
                state.taskApplications = action.payload.data.applications;
                state.pagination = action.payload.data.pagination;
            })
            .addCase(getTaskApplications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch task applications';
            })
            
            // Review application
            .addCase(reviewApplication.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(reviewApplication.fulfilled, (state, action) => {
                state.loading = false;
                // Update the application in both lists
                const updatedApplication = action.payload.data;
                const updateApplicationInList = (applications: TaskApplication[]) => {
                    const index = applications.findIndex(app => app._id === updatedApplication._id);
                    if (index !== -1) {
                        applications[index] = updatedApplication;
                    }
                };
                updateApplicationInList(state.userApplications);
                updateApplicationInList(state.taskApplications);
            })
            .addCase(reviewApplication.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to review application';
            })
            
            // Invite users to task
            .addCase(inviteUsersToTask.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(inviteUsersToTask.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(inviteUsersToTask.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to invite users';
            });
    }
});

export const { clearError, clearUserApplications, clearTaskApplications } = taskApplicationSlice.actions;
export default taskApplicationSlice.reducer;
